import type { MbcAdminCriterion, MbcBriefField } from "@nlc/shared-types";

import {
  MBC_BRIEF_FIELD_LABELS,
  MBC_JUDGED_BRIEF_FIELDS,
} from "@nlc/shared-types";
import { Check, Trash2 } from "lucide-react";
import { TableBody } from "react-aria-components";

import { Badge } from "@/components/badge";
import { Button } from "@/components/button";
import { Label } from "@/components/field";
import { NumberField } from "@/components/number-field";
import {
  Select,
  SelectButton,
  SelectListBox,
  SelectListItem,
  SelectPopover,
} from "@/components/select";
import { Switch } from "@/components/switch";
import { Cell, Column, Row, Table, TableHeader } from "@/components/table";

import { BRIEF_FIELD_OPTIONS } from "../brief-fields";
import { REQUIRED_TOTAL_WEIGHT, totalWeight } from "./guideline-body";

const EMPTY_CELL = <span aria-hidden>—</span>;

interface GuidelineCriteriaSectionProps {
  readonly criteria: MbcAdminCriterion[];
  readonly onChange: (criteria: MbcAdminCriterion[]) => void;
}

export function GuidelineCriteriaSection({
  criteria,
  onChange,
}: GuidelineCriteriaSectionProps) {
  const total = totalWeight(criteria);
  const unusedFields = BRIEF_FIELD_OPTIONS.filter(
    ({ id }) => !criteria.some(({ briefField }) => briefField === id),
  );
  const isBalanced = total === REQUIRED_TOTAL_WEIGHT;

  const updateCriterion = (
    briefField: MbcAdminCriterion["briefField"],
    changes: Partial<MbcAdminCriterion>,
  ) =>
    onChange(
      criteria.map((criterion) =>
        criterion.briefField === briefField
          ? { ...criterion, ...changes }
          : criterion,
      ),
    );

  return (
    <section>
      <div className="flex flex-wrap items-start justify-between gap-md p-6">
        <div>
          <h2 className="text-md font-semibold text-text-primary">Criteria</h2>
          <p className="text-sm text-text-tertiary">
            The weight is its percentage of the quality score and the weights
            must add up to 100. A required criterion must reach its min score
            (80 when empty) to submit the brief.
          </p>
        </div>
        <Badge variant={isBalanced ? "success" : "error"}>
          {isBalanced && <Check aria-hidden className="size-3" />}
          Total weight: {total}%
        </Badge>
      </div>

      <Table aria-label="Criteria of this guideline">
        <TableHeader>
          <Column defaultWidth="2fr" id="briefField" isRowHeader minWidth={160}>
            Brief field
          </Column>
          <Column defaultWidth={130} id="weight" minWidth={120}>
            Weight (%)
          </Column>
          <Column defaultWidth={100} id="required" minWidth={90}>
            Required
          </Column>
          <Column defaultWidth={130} id="minScore" minWidth={120}>
            Min score
          </Column>
          <Column defaultWidth={64} id="remove" minWidth={64}>
            <span className="sr-only">Remove</span>
          </Column>
        </TableHeader>
        <TableBody
          items={criteria.map((criterion) => ({
            ...criterion,
            id: criterion.briefField,
          }))}
          renderEmptyState={() => (
            <p className="p-3xl text-sm text-text-tertiary">
              Add a criterion below to score a brief field.
            </p>
          )}
        >
          {(criterion) => {
            const label = MBC_BRIEF_FIELD_LABELS[criterion.briefField];

            return (
              <Row id={criterion.id}>
                <Cell>{label}</Cell>
                <Cell>
                  <NumberField
                    aria-label={`Weight for ${label}`}
                    className="w-24"
                    formatOptions={{ maximumFractionDigits: 0 }}
                    maxValue={100}
                    minValue={0}
                    onChange={(weight) =>
                      updateCriterion(criterion.briefField, {
                        weight: Number.isNaN(weight) ? 0 : weight,
                      })
                    }
                    step={1}
                    value={criterion.weight}
                  />
                </Cell>
                <Cell>
                  <Switch
                    aria-label={`${label} required to submit`}
                    isSelected={criterion.required}
                    onChange={(required) =>
                      updateCriterion(criterion.briefField, { required })
                    }
                  />
                </Cell>
                <Cell>
                  {MBC_JUDGED_BRIEF_FIELDS.includes(criterion.briefField) ? (
                    <NumberField
                      aria-label={`Min score for ${label}`}
                      className="w-24"
                      maxValue={100}
                      minValue={0}
                      onChange={(minScore) =>
                        updateCriterion(criterion.briefField, {
                          minScore: Number.isNaN(minScore) ? null : minScore,
                        })
                      }
                      value={criterion.minScore ?? Number.NaN}
                    />
                  ) : (
                    EMPTY_CELL
                  )}
                </Cell>
                <Cell>
                  <Button
                    aria-label={`Remove ${label}`}
                    color="destructive"
                    isIconOnly
                    onPress={() =>
                      onChange(
                        criteria.filter(
                          ({ briefField }) =>
                            briefField !== criterion.briefField,
                        ),
                      )
                    }
                    size="sm"
                    variant="tertiary"
                  >
                    <Trash2 size={16} />
                  </Button>
                </Cell>
              </Row>
            );
          }}
        </TableBody>
      </Table>

      {unusedFields.length > 0 && (
        <div className="border-t border-border-secondary p-6">
          <Select
            onSelectionChange={(key) =>
              onChange([
                ...criteria,
                {
                  briefField: key as MbcBriefField,
                  required: true,
                  weight: 0,
                },
              ])
            }
            placeholder="Choose a brief field"
            selectedKey={null}
          >
            <Label>Add criterion</Label>
            <SelectButton />
            <SelectPopover>
              <SelectListBox items={unusedFields}>
                {(item) => (
                  <SelectListItem id={item.id} textValue={item.label}>
                    {item.label}
                  </SelectListItem>
                )}
              </SelectListBox>
            </SelectPopover>
          </Select>
        </div>
      )}
    </section>
  );
}
